import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Button } from "../../../components/ui/button";
import { Lock, Shield, HelpCircle } from 'lucide-react';
import { useTheme } from "../../Landing/theme-provider";

// Type assertions for JS components
const TypedButton = Button as React.ComponentType<React.ButtonHTMLAttributes<HTMLButtonElement> & { className?: string; variant?: string; size?: string; children?: React.ReactNode }>;

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// CSS Variables for spacing (8px increments)
const spacing = {
  xs: '8px',
  sm: '16px',
  md: '24px',
  lg: '32px',
  xl: '48px',
  xxl: '72px',
};

const Container = styled.section<{ isDarkMode: boolean }>`
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100vw;
  background: ${props => props.isDarkMode ? '#0f172a' : '#ffffff'};
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${spacing.lg};
  display: grid;
  grid-template-columns: 7fr 5fr;
  gap: ${spacing.xl};
  align-items: flex-start;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: ${spacing.lg};
    padding: 0 ${spacing.md};
    max-width: 600px;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    padding: 0 ${spacing.sm};
  }

  @media (max-width: 480px) {
    padding: 0 12px;
    gap: ${spacing.md};
  }
`;

const LeftContent = styled.article`
  animation: ${fadeInUp} 0.8s ease-out;
  position: relative;
  
  @media (max-width: 1024px) {
    order: 2;
  }
`;

const FounderNoteCard = styled.div<{ isDarkMode: boolean }>`
  position: relative;
  background: ${props => props.isDarkMode ? '#1e293b' : '#ffffff'};
  border-radius: 16px;
  padding: ${spacing.xl};
  border: 1px solid ${props => props.isDarkMode ? '#374151' : '#e5e7eb'};

  @media (max-width: 480px) {
    padding: ${spacing.md};
    border-radius: 12px;
  }
`;

const NoteHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.md};
  margin-bottom: ${spacing.lg};
`;

const FounderAvatar = styled.div`
  width: 83px;
  height: 83px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(221, 214, 254, 0.3);
  flex-shrink: 0;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  &:hover::after {
    content: 'Founder, SmushLabs';
    position: absolute;
    bottom: -30px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    white-space: nowrap;
    pointer-events: none;
  }
`;

const NoteTitle = styled.h1<{ isDarkMode: boolean }>`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 34px;
  font-weight: 600;
  color: ${props => props.isDarkMode ? '#f1f5f9' : '#0f172a'};
  margin: 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 28px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const NoteSubhead = styled.p<{ isDarkMode: boolean }>`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: ${props => props.isDarkMode ? '#94a3b8' : '#64748b'};
  margin: ${spacing.sm} 0 ${spacing.lg} 0;
  line-height: 1.5;
`;

const NoteContent = styled.div<{ isDarkMode: boolean }>`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: ${props => props.isDarkMode ? '#cbd5e1' : '#334155'};
  
  p {
    margin-bottom: ${spacing.md};
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-weight: 600;
    color: ${props => props.isDarkMode ? '#f1f5f9' : '#0f172a'};
  }
  
  a {
    color: #ddd6fe;
    text-decoration: none;
    border-bottom: 1px solid rgba(221, 214, 254, 0.4);
    transition: border-color 0.2s;
    
    &:hover {
      border-bottom-color: #ddd6fe;
    }
    
    &:focus {
      outline: 2px solid #ddd6fe;
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  @media (max-width: 480px) {
    font-size: 14px;

    p {
      margin-bottom: ${spacing.sm};
    }
  }
`;

const SectionHeading = styled.h2<{ isDarkMode: boolean }>`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.isDarkMode ? '#f1f5f9' : '#0f172a'};
  margin: ${spacing.xl} 0 ${spacing.md} 0;
  
  &:first-of-type {
    margin-top: ${spacing.lg};
  }

  @media (max-width: 480px) {
    font-size: 20px;
    margin: ${spacing.lg} 0 ${spacing.sm} 0;
  }
`;

const BulletList = styled.ul<{ isDarkMode: boolean }>`
  list-style: none;
  padding: 0;
  margin: ${spacing.sm} 0;
  
  li {
    padding-left: ${spacing.md};
    position: relative;
    margin-bottom: ${spacing.xs};
    color: ${props => props.isDarkMode ? '#cbd5e1' : '#334155'};
    font-size: 16px;
    line-height: 1.5;
    
    &::before {
      content: '•';
      position: absolute;
      left: 0;
      color: #ddd6fe;
      font-weight: bold;
    }
  }
`;

const Signature = styled.div`
  margin-top: ${spacing.xl};
  padding-top: ${spacing.lg};
  border-top: 1px solid rgba(221, 214, 254, 0.2);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const SignatureImage = styled.img`
  height: 48px;
  margin-bottom: ${spacing.sm};
`;

const SignatureText = styled.div<{ isDarkMode: boolean }>`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: ${props => props.isDarkMode ? '#cbd5e1' : '#334155'};
  margin-bottom: 4px;
`;

const SignatureTitle = styled.p<{ isDarkMode: boolean }>`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.isDarkMode ? '#94a3b8' : '#64748b'};
  margin: 0;
`;

const RightContent = styled.aside`
  position: sticky;
  top: 0;
  align-self: flex-start;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
  max-width: 100%;
  
  @media (max-width: 1024px) {
    position: relative;
    top: 0;
    order: 1;
    width: 100%;
  }
`;

const PlanCard = styled.div<{ isDarkMode: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  padding: ${spacing.md};
  background: ${props => props.isDarkMode ? '#1e293b' : '#ffffff'};
  border: 1px solid ${props => props.isDarkMode ? '#374151' : '#e5e7eb'};

  @media (max-width: 480px) {
    padding: ${spacing.sm};
    border-radius: 12px;
  }
`;

const PlanBadge = styled.div<{ isDarkMode: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 9999px;
  padding: 6px 14px;
  font-size: 11px;
  font-weight: 600;
  background: linear-gradient(135deg, #ddd6fe, #c4b5fd);
  color: #0f172a;
  margin-bottom: 6px;
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  width: fit-content;
  box-shadow: 0 2px 8px rgba(221, 214, 254, 0.3);
`;

const PlanHero = styled.div`
  margin-bottom: ${spacing.sm};
`;

const PlanHeroTitle = styled.h2<{ isDarkMode: boolean }>`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.isDarkMode ? '#f1f5f9' : '#0f172a'};
  margin: 0 0 ${spacing.xs} 0;
  line-height: 1.2;
`;

const PlanHeroSubline = styled.p<{ isDarkMode: boolean }>`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  color: ${props => props.isDarkMode ? '#94a3b8' : '#64748b'};
  margin: 0;
  line-height: 1.5;
`;

const PlanPrice = styled.div`
  margin-bottom: ${spacing.xs};
`;

const PriceAmount = styled.div<{ isDarkMode: boolean }>`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 48px;
  font-weight: 700;
  color: ${props => props.isDarkMode ? '#f1f5f9' : '#0f172a'};
  line-height: 1;
  margin-bottom: 4px;

  @media (max-width: 480px) {
    font-size: 38px;
  }
`;

const LimitedSeatsText = styled.div<{ isDarkMode: boolean }>`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${props => props.isDarkMode ? '#cbd5e1' : '#475569'};
  letter-spacing: 0.8px;
  margin-top: 0;
  margin-bottom: ${spacing.sm};
  text-transform: uppercase;
`;

const PricePeriod = styled.div<{ isDarkMode: boolean }>`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  color: ${props => props.isDarkMode ? '#94a3b8' : '#64748b'};
  margin-bottom: 0;
`;

const EnrollmentNote = styled.p<{ isDarkMode: boolean }>`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 13px;
  color: ${props => props.isDarkMode ? '#94a3b8' : '#64748b'};
  margin: ${spacing.sm} 0 0 0;
  line-height: 1.5;
`;



const ComparisonTable = styled.div<{ isDarkMode: boolean }>`
  margin: ${spacing.sm} 0 ${spacing.xs} 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.isDarkMode ? '#1e293b' : '#f1f5f9'};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.isDarkMode ? '#475569' : '#cbd5e1'};
    border-radius: 3px;
  }
`;

const Table = styled.table<{ isDarkMode: boolean }>`
  width: 100%;
  min-width: 320px;
  border-collapse: collapse;
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 13px;

  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const TableHeader = styled.thead<{ isDarkMode: boolean }>`
  th {
    padding: ${spacing.sm} ${spacing.xs};
    text-align: center;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${props => props.isDarkMode ? '#94a3b8' : '#64748b'};
    border-bottom: 1px solid ${props => props.isDarkMode ? 'rgba(221, 214, 254, 0.2)' : 'rgba(221, 214, 254, 0.1)'};
    
    &:first-child {
      text-align: left;
    }
    
    &:nth-child(2) {
      background: linear-gradient(135deg, #ddd6fe, #c4b5fd);
      color: #0f172a;
      font-weight: 700;
      border: 2px solid #ddd6fe;
      border-bottom: none;
    }
  }
`;

const TableBody = styled.tbody<{ isDarkMode: boolean }>`
  td {
    padding: ${spacing.sm} ${spacing.xs};
    text-align: center;
    color: ${props => props.isDarkMode ? '#cbd5e1' : '#334155'};
    border-bottom: 1px solid ${props => props.isDarkMode ? 'rgba(221, 214, 254, 0.1)' : 'rgba(221, 214, 254, 0.05)'};
    
    &:first-child {
      text-align: left;
    }
    
    &:nth-child(2) {
      background: ${props => props.isDarkMode ? 'rgba(221, 214, 254, 0.12)' : 'rgba(221, 214, 254, 0.08)'};
      font-weight: 600;
      color: ${props => props.isDarkMode ? '#f1f5f9' : '#0f172a'};
      border-left: 2px solid ${props => props.isDarkMode ? 'rgba(221, 214, 254, 0.3)' : 'rgba(221, 214, 254, 0.4)'};
      border-right: 2px solid ${props => props.isDarkMode ? 'rgba(221, 214, 254, 0.3)' : 'rgba(221, 214, 254, 0.4)'};
    }
    
    &:last-child {
      border-bottom: none;
    }
  }
`;

// SeatCounter component - removed as it's no longer used in the UI

const TrustSignals = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  margin: ${spacing.sm} 0;
  padding: 12px 0;
  border-top: 1px solid rgba(221, 214, 254, 0.1);
  border-bottom: 1px solid rgba(221, 214, 254, 0.1);
`;

const TrustBadge = styled.div<{ isDarkMode: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  color: ${props => props.isDarkMode ? '#94a3b8' : '#64748b'};
  
  svg {
    width: 14px;
    height: 14px;
    color: #ddd6fe;
  }
`;

const PlanFooter = styled.div<{ isDarkMode: boolean }>`
  margin-top: ${spacing.sm};
  padding-top: ${spacing.sm};
  border-top: 1px solid ${props => props.isDarkMode ? 'rgba(221, 214, 254, 0.2)' : 'rgba(221, 214, 254, 0.1)'};
`;

const CTAButton = styled(TypedButton, { shouldForwardProp: (prop) => prop !== 'isDarkMode' }) <{ isDarkMode: boolean }>`
  width: 100%;
  height: 48px;
  font-family: var(--font-sans);
  font-weight: 500;
  font-size: 15px;
  border-radius: var(--smush-cta-radius);
  background: var(--smush-cta-primary);
  color: #ffffff;
  border: none;
  box-shadow: none;
  transition: background-color 0.2s ease;
  margin-bottom: ${spacing.sm};
  
  &:hover {
    background: var(--smush-cta-primary-hover);
  }
  
  &:focus {
    outline: 2px solid #ddd6fe;
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    height: 48px;
    font-size: 15px;
  }
`;

const SecondaryButton = styled(TypedButton, { shouldForwardProp: (prop) => prop !== 'isDarkMode' }) <{ isDarkMode: boolean }>`
  width: 100%;
  height: 48px;
  font-family: var(--font-sans);
  font-weight: 500;
  font-size: 15px;
  border-radius: var(--smush-cta-radius);
  background: var(--smush-cta-secondary);
  color: var(--smush-cta-primary);
  border: 1px solid var(--smush-cta-secondary-border);
  transition: background-color 0.2s ease, border-color 0.2s ease;
  
  &:hover {
    background: var(--smush-cta-secondary-hover);
    border-color: rgba(45, 54, 71, 0.34);
  }
  
  &:focus {
    outline: 2px solid #ddd6fe;
    outline-offset: 2px;
  }
`;

const SecurityNote = styled.div<{ isDarkMode: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: ${spacing.sm};
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 11px;
  color: ${props => props.isDarkMode ? '#94a3b8' : '#64748b'};
  justify-content: center;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const CtaSection: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  // Regional pricing state
  const [pricing, setPricing] = React.useState({
    symbol: '$',
    price: 947,
  });

  // Fetch regional pricing on mount
  React.useEffect(() => {
    const fetchPricing = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const regionParam = urlParams.get('region');
        const apiUrl = regionParam ? `/api/pricing?region=${regionParam}` : '/api/pricing';

        const response = await fetch(apiUrl);
        if (response.ok) {
          const data = await response.json();
          if (data.plans && data.plans.lifetime) {
            setPricing({
              symbol: data.symbol,
              price: data.plans.lifetime.price
            });
          }
        }
      } catch (error) {
        console.log('Using default USD pricing');
      }
    };
    fetchPricing();
  }, []);

  const lifetimePlan = {
    name: "Pro+ Founders Edition",
    price: `${pricing.symbol}${pricing.price}`,
    description: "Limited to 50 seats • One-time payment",
    features: [
      "5 chatbots",
      "15 lead generation forms",
      "100,000 monthly smart responses",
      "250 file uploads",
      "2,000 trainable URLs",
      "Advanced analytics + CSV exports",
      "Advanced branding controls",
      "Priority support queue",
      "3 team seats",
      "Private Founders group",
      "Roadmap previews & early betas",
      "API access (when released)"
    ],
    badge: "Founders Edition",
    note: "One-time payment, lifetime access • 14-day refund window"
  };

  const comparisonData = [
    { feature: 'Chatbots', pro: '3', founders: '5', unlimited: 'Unlimited' },
    { feature: 'Smart Responses', pro: '50,000/mo', founders: '100,000/mo', unlimited: 'Unlimited' },
    { feature: 'File Uploads', pro: '100', founders: '250', unlimited: 'Unlimited' },
    { feature: 'Trainable URLs', pro: '1,000', founders: '2,000', unlimited: 'Unlimited' },
    { feature: 'Analytics', pro: 'Advanced', founders: 'Advanced + CSV', unlimited: 'Advanced + CSV' },
    { feature: 'Support', pro: 'Standard', founders: 'Priority', unlimited: 'Priority' },
    { feature: 'Feature Requests', pro: 'Standard', founders: 'Priority consideration', unlimited: 'Discuss with us' },
    { feature: 'Community', pro: '—', founders: 'Private group', unlimited: 'Private group' },
    { feature: 'Price', pro: '$38/mo', founders: `${lifetimePlan.price} once`, unlimited: 'Custom' },
  ];

  const handleGetStarted = () => {
    const planData = {
      id: "lifetime",
      name: lifetimePlan.name,
      price: lifetimePlan.price,
      description: lifetimePlan.description,
      features: lifetimePlan.features,
      planType: "lifetime"
    };
    localStorage.setItem('selectedPlan', JSON.stringify(planData));
    window.location.href = '/get-started';
  };

  const handleAskQuestion = () => {
    window.location.href = 'mailto:support@smushlabs.ai?subject=Question%20about%20SmushChat%20Founders%20Edition';
  };

  return (
    <Container id="lifetime-deal" isDarkMode={isDarkMode}>
      <ContentWrapper>
        <LeftContent>
          <FounderNoteCard isDarkMode={isDarkMode}>
            <NoteHeader>
              <FounderAvatar>
                <img
                  src="/images/founder-asif-2025.png"
                  alt="Asif Farhan Khan"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </FounderAvatar>
              <div>
                <NoteTitle isDarkMode={isDarkMode}>Founder's Note</NoteTitle>
                <NoteSubhead isDarkMode={isDarkMode}>
                  Get SmushChat Founders Edition
                </NoteSubhead>
              </div>
            </NoteHeader>

            <NoteContent isDarkMode={isDarkMode}>
              <p>
                <strong>I began as a freelancer designing automation for founders who cared more about outcomes than noise.</strong> That work shaped SmushLabs: build practical tools, explain them clearly, and earn trust through the product rather than inflated claims.
              </p>

              <SectionHeading isDarkMode={isDarkMode}>Why SmushChat ?</SectionHeading>

              <p>
                I saw two major problems in the chatbot space:
              </p>

              <BulletList isDarkMode={isDarkMode}>
                <li><strong>High premiums for basic features.</strong> Industry leaders charge enterprise prices and force-bundle features that should be affordable and accessible to every founder.</li>
                <li><strong>Outgrowing Reactive Chatbots:</strong> Support is evolving beyond smarter look-ups. The next chapter is proactive intelligence, systems that understand your product, anticipate what customers need, and guide the experience so smoothly it feels invisible. That's the future SmushChat is being built toward.</li>
              </BulletList>


              <SectionHeading isDarkMode={isDarkMode}>Why this Founders Edition exists</SectionHeading>

              <p>
                Most chatbot tools force you to adapt to their limitations. We're doing the opposite. SmushChat is being built with your feedback! Businesses from different spaces who all share one thing: they need reliable automation that actually solves real problems.
              </p>

              <p>
                As part of the Founders Edition, your needs guide the roadmap. If something matters to your workflow, it gets built and we handle all the setup for you.
              </p>

              <p>
                In return for a handful of feedback touchpoints, you get lifetime access to our best tier: advanced features, priority support, personalized onboarding, and direct influence on development.
              </p>

              {/* Temporarily hidden: Founders Edition feedback commitment details.
              <SectionHeading isDarkMode={isDarkMode}>Your commitment (simple & finite)</SectionHeading>

              <BulletList isDarkMode={isDarkMode}>
                <li>Four short feedback sessions in 12 months (30-minute calls or async option)</li>
                <li>Occasional quick surveys on specific features</li>
                <li>Optional recognition in changelogs/community (you can opt out)</li>
              </BulletList>

              <p>
                After 12 months, there's no obligation — only lifetime access, all future updates, features developed on your requests, and all the benefits you've already unlocked.
              </p>
              */}

              {/* <SectionHeading isDarkMode={isDarkMode}>How does this compare to Pro Plan?</SectionHeading>
              
              <p>
                Pro Plan users at $23/month get great value for ongoing flexibility. But Founders Edition users get <strong>5 chatbots instead of 3</strong>, <strong>double the smart responses</strong>, <strong>priority support</strong>, <strong>direct roadmap influence</strong>, and access to a private founder community — all for a one-time payment. It's designed for founders who want to be part of building the product, not just using it.
              </p> */}


            </NoteContent>

            <Signature>
              <SignatureImage
                src="/images/signature.png"
                alt="Asif Farhan Khan's signature"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const textSignature = document.getElementById('text-signature');
                  if (textSignature) textSignature.style.display = 'block';
                }}
              />
              <SignatureText id="text-signature" isDarkMode={isDarkMode} style={{ display: 'none' }}>
                Asif Farhan Khan
              </SignatureText>
              <SignatureTitle isDarkMode={isDarkMode}>Founder, SmushLabs</SignatureTitle>
            </Signature>
          </FounderNoteCard>
        </LeftContent>

        <RightContent>
          <PlanCard isDarkMode={isDarkMode}>
            <PlanBadge isDarkMode={isDarkMode}>
              {lifetimePlan.badge}
            </PlanBadge>

            <LimitedSeatsText isDarkMode={isDarkMode}>
              Limited Seats Available
            </LimitedSeatsText>

            <PlanHero>
              <PlanHeroTitle isDarkMode={isDarkMode}>
                Own SmushChat. Forever.
              </PlanHeroTitle>
              <PlanHeroSubline isDarkMode={isDarkMode}>
                Pro+ Founders Edition — one-time {lifetimePlan.price}. Limited to 50 seats.
              </PlanHeroSubline>
            </PlanHero>

            <PlanPrice>
              <PriceAmount isDarkMode={isDarkMode}>{lifetimePlan.price}</PriceAmount>
              <PricePeriod isDarkMode={isDarkMode}>One-time payment</PricePeriod>
              <EnrollmentNote isDarkMode={isDarkMode}>
                Once enrolled, you'll receive an email within 24 hours to schedule your design and deployment, along with all details regarding your lifetime subscription.
              </EnrollmentNote>
            </PlanPrice>



            <ComparisonTable isDarkMode={isDarkMode}>
              <Table isDarkMode={isDarkMode}>
                <TableHeader isDarkMode={isDarkMode}>
                  <tr>
                    <th>Feature</th>
                    <th>Pro+ LTD</th>
                    <th>Pro Monthly</th>
                  </tr>
                </TableHeader>
                <TableBody isDarkMode={isDarkMode}>
                  {comparisonData.map((row, index) => (
                    <tr key={index}>
                      <td>{row.feature}</td>
                      <td>{row.founders}</td>
                      <td>{row.pro}</td>
                    </tr>
                  ))}
                </TableBody>
              </Table>
            </ComparisonTable>

            <TrustSignals>
              <TrustBadge isDarkMode={isDarkMode}>
                <Shield size={14} />
                <span>Privacy policy published</span>
              </TrustBadge>
              <TrustBadge isDarkMode={isDarkMode}>
                <Lock size={14} />
                <span>Encrypted payments</span>
              </TrustBadge>
            </TrustSignals>

            <PlanFooter isDarkMode={isDarkMode}>
              <CTAButton
                isDarkMode={isDarkMode}
                onClick={handleGetStarted}
                size="lg"
              >
                Get Lifetime Access — {lifetimePlan.price}
              </CTAButton>

              <SecondaryButton
                isDarkMode={isDarkMode}
                onClick={handleAskQuestion}
              >
                <HelpCircle size={16} style={{ marginRight: '8px' }} />
                Ask a question
              </SecondaryButton>

              <SecurityNote isDarkMode={isDarkMode}>
                <Lock size={12} />
                <span>Secure checkout</span>
              </SecurityNote>
            </PlanFooter>
          </PlanCard>
        </RightContent>
      </ContentWrapper>
    </Container>
  );
};

export default CtaSection;
